import React from "react";
import { Card, Divider, Typography } from "antd";
import { KPI_INDICATORS } from "../config/constants";

const { Text } = Typography;

/**
 * Review step shared by Create Workspace and Add Market.
 *
 * It is the operator's last chance to check what will be written, so it echoes
 * every value the payload carries. The create-only fields (refresh periods,
 * auto refresh) and the optional contract range render only when their caller
 * supplies them, so the other two wizards gain no blank rows.
 */
const Step4 = ({
  getPairs,
  client,
  workspaceName,
  customRegions,
  marketAssignments,
  marketWeights,
  calcTotal,
  refreshPeriods,
  autoRefresh,
  contractMonthRange,
}) => {
  const pairs = getPairs();
  const row = { marginTop: 8 };
  const hasContractRange = !!(contractMonthRange?.[0] && contractMonthRange?.[1]);

  const formatMonth = (m) => m.format("YYYY-MM");

  return (
    <Card
      title="Review Workspace"
      style={{ marginTop: 24 }}
    >
      <Divider style={{ marginTop: 0 }} />
      <div style={{ background: "#f8f9fa", padding: 16, borderRadius: 6 }}>
        <Text strong>Summary</Text>
        <div style={{ marginTop: 8 }}>
          <div>Workspace (id_client): {workspaceName}</div>
          <div>Client (mapped_id_client): {client?.name || "-"}</div>
          <div style={row}>
            Regions:{" "}
            {Object.values(customRegions)
              .map((r) => r.name)
              .join(", ")}
          </div>
          <div style={row}>
            Markets:
            <ul style={{ margin: "6px 0 0 18px" }}>
              {Object.entries(customRegions).map(([rid, r]) => (
                <li key={rid}>
                  {r.name}:{" "}
                  {(r.markets || []).map((m) => m.toUpperCase()).join(", ")}
                </li>
              ))}
            </ul>
          </div>

          {refreshPeriods !== undefined && (
            <div style={row}>
              Refresh periods:{" "}
              {refreshPeriods?.length ? refreshPeriods.join(", ") : "none"}
            </div>
          )}
          {autoRefresh !== undefined && (
            <div style={row}>Auto refresh: {autoRefresh ? "On" : "Off"}</div>
          )}
          {hasContractRange && (
            <div style={row}>
              Contract months: {formatMonth(contractMonthRange[0])} to{" "}
              {formatMonth(contractMonthRange[1])}
            </div>
          )}

          <div style={{ marginTop: 12 }}>
            <Text strong>Weights:</Text>
            <ul style={{ margin: "6px 0 0 18px" }}>
              {pairs.map(({ regionId, regionName, market }) => {
                const ck = `${regionId}|${market}`;
                const total = calcTotal(ck);
                const weights = marketWeights?.[ck] || {};
                const balanced = Math.abs(total - 100) < 0.1;
                return (
                  <li
                    key={ck}
                    style={{ marginBottom: 4 }}
                  >
                    <span style={{ fontWeight: 600 }}>
                      {regionName} — {market.toUpperCase()}:
                    </span>{" "}
                    <Text type={balanced ? undefined : "danger"}>
                      total {total.toFixed(1)}%
                    </Text>
                    <div style={{ fontSize: 12, color: "#595959" }}>
                      {KPI_INDICATORS.map(
                        (k) =>
                          `${k.label} ${Number(weights[k.key] ?? 0).toFixed(1)}%`
                      ).join(" · ")}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          <div style={{ marginTop: 12 }}>
            <Text strong>Assignments:</Text>
            <ul style={{ margin: "6px 0 0 18px" }}>
              {pairs.map(({ regionId, regionName, market }) => {
                const ck = `${regionId}|${market}`;
                const a = marketAssignments[ck];
                return (
                  <li
                    key={ck}
                    style={{ marginBottom: 4 }}
                  >
                    <span style={{ fontWeight: 600 }}>
                      {regionName} — {market.toUpperCase()}:
                    </span>{" "}
                    {a ? (
                      <span>
                        Groups[
                        {(a.coreBrandGroups || [])
                          .map((g) => g.name)
                          .join("; ")}
                        ]; Company[{a.company || "-"}]; Competitors[
                        {(a.competitors || [])
                          .map((c) =>
                            c.brand
                              ? `${c.competitor}/${c.brand}`
                              : c.competitor
                          )
                          .join("; ") || "-"}
                        ]
                      </span>
                    ) : (
                      <span>-</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default Step4;
